import { getMenuItems } from './getMenuItems';
import { initializeView } from './initializeView';
import { updateSelectedViewDate } from './updateSelectedViewDate';
import { BaseCalendarScenarioId } from '../constants';
import { CalendarPayload, CalendarViewType, UserEvent } from '../schema';
import { getNumDaysWeekViews, isDayGridViewType } from '../utils/calendarViewTypeUtils';
import { getStartOfWeek, getWorkDays, convertToOwaDate } from '../utils/dateUtils';
import { updateCalendarIds } from 'owa-agenda-events-list';
import { getAllCalendars, removeCalendarFromCalendarsCache } from 'owa-calendar-cache';
import { calendarCacheInitializedForAccount } from 'owa-calendar-cache-loader';
import { OwaDate, asDate } from 'owa-datetime';
import { observableTodayChanged, observableToday } from 'owa-observable-datetime';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import { useAssetCommands, useAssetId, usePatchPartialPayload } from 'owa-timestream-asset-sdk';
import { navigateCalendarAssetToDate, setCanvasLock } from 'owa-timestream-asset-sdk-actions';
import { CalendarAssetCommandDescriptors } from 'owa-timestream-asset-sdk-calendarevents';
import { getFolderIdByCalendarID, initializeCalendars } from 'owa-timestream-asset-sdk-calendars';
import * as React from 'react';
import type { PerformanceDatapoint } from 'owa-analytics';
import { convertIdsToExpectedFormat, convertIdSource } from 'owa-immutable-id';
import { getUserMailboxInfo } from 'owa-client-ids';

export default function useCalendarViewState(
    { payload }: AssetCommonViewProps<CalendarPayload>,
    assetPerfDatapoint: PerformanceDatapoint
) {
    const { calendarIds, viewType, numDaysInDayView, title } = payload;

    const scenarioId = BaseCalendarScenarioId + '-' + useAssetId();

    const patchPartialPayload = usePatchPartialPayload<CalendarPayload>();

    const today = observableToday();
    const [selectedDate, setSelectedDate] = React.useState<Date>(asDate(today));
    const owaSelectedDate = convertToOwaDate(selectedDate);

    React.useEffect(() => {
        const hasSelectedCalendars = calendarIds.length == 0;
        assetPerfDatapoint.addCustomProperty('viewType', viewType);
        assetPerfDatapoint.addCustomProperty('hasSelectedCalendars', hasSelectedCalendars);
        async function formatCalendarIds() {
            // VSO 117007 - add payload prop for connected account calendar ids, use that here when converting calendar ids
            const formattedResult = await convertIdsToExpectedFormat(
                payload.calendarIds,
                getUserMailboxInfo().userIdentity,
                convertIdSource.BoardCalendarAsset
            );
            if (formattedResult.didConvert) {
                initializeCalendars();
                initializeView(
                    scenarioId,
                    viewType,
                    owaSelectedDate,
                    formattedResult.ids,
                    numDaysInDayView,
                    assetPerfDatapoint
                );
                patchPartialPayload({ calendarIds: formattedResult.ids });
            } else {
                initializeCalendars();
                initializeView(
                    scenarioId,
                    viewType,
                    owaSelectedDate,
                    calendarIds,
                    numDaysInDayView,
                    assetPerfDatapoint
                );
            }
        }
        formatCalendarIds();
    }, []);

    // We need to set up an effect to force-rerender day grid views when certain values and settings change,
    // since the component has not been refactored/fixed yet to properly auto-rerender as needed
    // For workweek view type, we need to completely re-init to correctly update surface to reflect workweek settings
    const workDays = getWorkDays();
    // NOTE: startOfWeek effect dep is a date-time string in order for deps comparison to not over-trigger
    const startOfWeek = getStartOfWeek(convertToOwaDate(selectedDate)).toString();
    React.useEffect(() => {
        if (isDayGridViewType(viewType)) {
            initializeView(scenarioId, viewType, owaSelectedDate, calendarIds, numDaysInDayView);
        }
    }, [workDays, startOfWeek, today]);

    // This effect is used to make sure that calendar widgets are in sync across browser windows.
    React.useEffect(() => {
        initializeView(scenarioId, viewType, owaSelectedDate, calendarIds, numDaysInDayView);
    }, [calendarIds]);

    // We need to set up an orchestrator to force-rerender day grid views when cache is initialized for an account,
    // since the component has not been refactored/fixed yet to properly auto-rerender as needed
    useOrchestrator(
        calendarCacheInitializedForAccount,
        () => {
            if (isDayGridViewType(viewType)) {
                initializeView(
                    scenarioId,
                    viewType,
                    owaSelectedDate,
                    calendarIds,
                    numDaysInDayView
                );
            }
        },
        [scenarioId, viewType, owaSelectedDate, calendarIds, numDaysInDayView]
    );

    const updateDateForView = React.useCallback(
        (scenarioId: string, viewType: CalendarViewType, date: OwaDate) => {
            setSelectedDate(asDate(date));
            updateSelectedViewDate(scenarioId, viewType, date);
        },
        []
    );

    const updateSelectedCalendars = React.useCallback(
        (selectedCalendarIds: string[]) => {
            patchPartialPayload({ calendarIds: selectedCalendarIds });
            updateCalendarIds(selectedCalendarIds, scenarioId);

            // We need to force re-render day grid views when calendar selection changes
            // since the component has not been refactored/fixed yet to properly auto-rerender as needed
            if (isDayGridViewType(viewType)) {
                initializeView(
                    scenarioId,
                    viewType,
                    owaSelectedDate,
                    selectedCalendarIds,
                    numDaysInDayView
                );
            }
        },
        [scenarioId, viewType, owaSelectedDate, numDaysInDayView, patchPartialPayload]
    );

    // Remove deleted calendars from the payload
    React.useEffect(() => {
        const allCalendarIds = getAllCalendars().map(calendar => calendar.id.id);
        const inactiveCalendars = calendarIds.filter(
            calendarId => !allCalendarIds.includes(calendarId)
        );

        if (inactiveCalendars.length > 0) {
            const activeCalendars = calendarIds.filter(
                calendarId => !inactiveCalendars.includes(calendarId)
            );

            updateSelectedCalendars(activeCalendars);
        }
    }, [calendarIds, updateSelectedCalendars]);

    useOrchestrator(
        observableTodayChanged,
        () => {
            const todayDate = observableToday();
            updateDateForView(scenarioId, viewType, todayDate);
        },
        [scenarioId, viewType, updateDateForView]
    );

    useOrchestrator(
        navigateCalendarAssetToDate,
        actionMessage => {
            updateDateForView(scenarioId, viewType, actionMessage.date);
        },
        [scenarioId, viewType, updateDateForView]
    );

    useOrchestrator(
        removeCalendarFromCalendarsCache,
        actionMessage => {
            const filteredCalendarIds = calendarIds.filter(
                calendarId => calendarId != actionMessage.calendar.id.id
            );

            if (filteredCalendarIds.length < calendarIds.length) {
                updateSelectedCalendars(filteredCalendarIds);
            }
        },
        [calendarIds, updateSelectedCalendars]
    );

    const onSelectDate = React.useCallback(
        (date: OwaDate) => {
            updateDateForView(scenarioId, viewType, date);
        },
        [scenarioId, viewType, updateDateForView]
    );

    const onSelectViewType = React.useCallback(
        (selection: CalendarViewType) => {
            if (selection == CalendarViewType.Week || selection == CalendarViewType.WorkWeek) {
                patchPartialPayload({
                    viewType: selection,
                    numDaysInDayView: getNumDaysWeekViews(selection),
                });
            } else {
                patchPartialPayload({ viewType: selection });
            }

            // unlock canvas view as switching calendars might cause widget resize
            setCanvasLock(false);

            initializeView(scenarioId, selection, owaSelectedDate, calendarIds, numDaysInDayView);
        },
        [scenarioId, viewType, owaSelectedDate, calendarIds, numDaysInDayView, patchPartialPayload]
    );

    const onChangeNumDaysInDayView = React.useCallback(
        (numDaysInDayView: number) => {
            patchPartialPayload({
                viewType: CalendarViewType.Day,
                numDaysInDayView: numDaysInDayView,
            });
            initializeView(
                scenarioId,
                CalendarViewType.Day,
                owaSelectedDate,
                calendarIds,
                numDaysInDayView
            );
        },
        [scenarioId, owaSelectedDate, calendarIds, patchPartialPayload]
    );

    useAssetCommands(
        [
            {
                ...CalendarAssetCommandDescriptors.switchCalendarView,
                getMenuItems: () =>
                    getMenuItems(
                        viewType,
                        numDaysInDayView,
                        onSelectViewType,
                        onChangeNumDaysInDayView
                    ),
            },
        ],
        [viewType, onSelectViewType, onChangeNumDaysInDayView, numDaysInDayView]
    );

    const onSelectCalendar = React.useCallback(
        (selection: string) => {
            return (event: UserEvent) => {
                event.preventDefault();
                const index = calendarIds.indexOf(selection);
                const calendarIdsCopy = calendarIds.slice();
                if (index < 0) {
                    calendarIdsCopy.push(selection);
                } else {
                    calendarIdsCopy.splice(index, 1);
                }

                updateSelectedCalendars(calendarIdsCopy);
            };
        },
        [calendarIds, updateSelectedCalendars]
    );

    const folderIds = calendarIds.map(
        calendarId => getFolderIdByCalendarID(calendarId) ?? undefined
    );

    const onChangeTitle = React.useCallback(
        (newTitle?: string) => {
            patchPartialPayload({
                title: newTitle,
            });
        },
        [patchPartialPayload]
    );

    return {
        scenarioId,
        calendarIds,
        folderIds,
        viewType,
        selectedDate,
        isLoaded: calendarIds.length > 0,
        onSelectCalendar,
        onSelectViewType,
        onSelectDate /** call this when the date changes in a subcomponent, it updates the asset's date to remain in sync with its subcomponents*/,
        onDateUpdated: setSelectedDate /** call this when the date changes in the asset, it updates the asset's date and notifies consumers of changes so they can remain in sync */,
        numDaysInDayView,
        title,
        onChangeTitle,
        assetPerfDatapoint,
    };
}
